import { getSessionStorage } from '../utils/web/storageAvailability';
/**
 * SessionManager - Centralized session and storage management
 *
 * This utility provides consistent session handling across different deployment targets
 * (Chrome extension, web widget, playground) by abstracting the storage mechanisms
 * and providing unified session cleanup and management.
 */

import type { StorageAdapter } from '../adapters/storage';
import { logger } from '../utils/core/logger';
import { conversationScope } from './conversationScope';

interface SessionManagerConfig {
    siteName: string;
    apiUrl: string;
    storageAdapter?: StorageAdapter;
}

export class SessionManager {
    private config: SessionManagerConfig;

    constructor(config: SessionManagerConfig) {
        this.config = config;
    }

    /**
     * Generate a unique session storage key for InboundXService
     */
    private getInboundXSessionKey(): string {
        const apiUrlHash = this.config.apiUrl.replace(/[^a-zA-Z0-9]/g, '_');
        return `inboundx_session_${conversationScope(this.config.siteName)}_${apiUrlHash}`;
    }

    /**
     * Generate chat state storage key for ChatStorageService
     */
    private getChatStateKey(): string {
        return `inboundx_chat_state_${conversationScope(this.config.siteName)}`;
    }

    /**
     * Get navigation marker key
     */
    private getNavigationMarkerKey(): string {
        return 'inboundx_navigation_marker';
    }

    /**
     * Clear all session-related data from sessionStorage
     * This includes InboundXService sessions, chat state, and navigation markers
     */
    clearSessionStorage(): void {
        try {
            // Clear InboundXService session
            getSessionStorage().removeItem(this.getInboundXSessionKey());

            // Clear ChatStorageService state
            getSessionStorage().removeItem(this.getChatStateKey());

            // Clear navigation markers
            getSessionStorage().removeItem(this.getNavigationMarkerKey());

            logger.debug('SessionManager: Cleared sessionStorage for', this.config.siteName);
        } catch (error) {
            logger.warn('SessionManager: Error clearing sessionStorage:', error);
        }
    }

    /**
     * Clear configuration data from the storage adapter (localStorage for web, chrome.storage for extension)
     */
    async clearStorageAdapter(): Promise<void> {
        if (!this.config.storageAdapter) {
            logger.warn('SessionManager: No storage adapter provided, skipping adapter cleanup');
            return;
        }

        try {
            const keysToRemove = [
                'conversationHistory',
                'chatMessages',
                'currentConversation',
                'widgetState',
                'isExpanded',
                'sessionId',
                'lastMessageId',
                'chatState',
            ];

            await this.config.storageAdapter.remove(keysToRemove);
            logger.debug('SessionManager: Cleared storage adapter for', this.config.siteName);
        } catch (error) {
            logger.warn('SessionManager: Error clearing storage adapter:', error);
        }
    }

    /**
     * Perform a complete session reset
     * This clears both sessionStorage and storage adapter data
     */
    async resetSession(): Promise<void> {
        logger.debug('SessionManager: Performing complete session reset for', this.config.siteName);

        // Clear sessionStorage (immediate)
        this.clearSessionStorage();

        // Clear storage adapter (async)
        await this.clearStorageAdapter();

        logger.debug('SessionManager: Session reset complete for', this.config.siteName);
    }

    /**
     * Reset session when API URL changes
     * This is specifically for cases where the endpoint changes but the site remains the same
     */
    async resetForApiChange(newApiUrl: string): Promise<void> {
        logger.debug(
            'SessionManager: Resetting session for API change from',
            this.config.apiUrl,
            'to',
            newApiUrl
        );

        // Clear current session data
        await this.resetSession();

        // Update config with new API URL
        this.config.apiUrl = newApiUrl;

        logger.debug('SessionManager: Session reset for API change complete');
    }

    /**
     * Reset session when site/client changes
     * This is specifically for cases where the site changes (e.g., different client in playground)
     */
    async resetForSiteChange(newSiteName: string): Promise<void> {
        logger.debug(
            'SessionManager: Resetting session for site change from',
            this.config.siteName,
            'to',
            newSiteName
        );

        // Clear current session data
        await this.resetSession();

        // Update config with new site name
        this.config.siteName = newSiteName;

        logger.debug('SessionManager: Session reset for site change complete');
    }

    /**
     * Update the configuration
     */
    updateConfig(newConfig: Partial<SessionManagerConfig>): void {
        this.config = { ...this.config, ...newConfig };
    }

    /**
     * Get current configuration
     */
    getConfig(): SessionManagerConfig {
        return { ...this.config };
    }

    /**
     * Check if there's an existing session in sessionStorage
     */
    hasExistingSession(): boolean {
        try {
            return getSessionStorage().getItem(this.getInboundXSessionKey()) !== null;
        } catch (error) {
            logger.warn('SessionManager: Error checking existing session:', error);
            return false;
        }
    }

    /**
     * Check if there's existing chat state in sessionStorage
     */
    hasExistingChatState(): boolean {
        try {
            return getSessionStorage().getItem(this.getChatStateKey()) !== null;
        } catch (error) {
            logger.warn('SessionManager: Error checking existing chat state:', error);
            return false;
        }
    }

    /**
     * Get debugging information about current storage state
     */
    getDebugInfo(): Record<string, unknown> {
        return {
            siteName: this.config.siteName,
            apiUrl: this.config.apiUrl,
            inboundxSessionKey: this.getInboundXSessionKey(),
            chatStateKey: this.getChatStateKey(),
            hasExistingSession: this.hasExistingSession(),
            hasExistingChatState: this.hasExistingChatState(),
            hasStorageAdapter: !!this.config.storageAdapter,
        };
    }
}

/**
 * Factory function to create a SessionManager instance
 */
export const createSessionManager = (config: SessionManagerConfig): SessionManager => {
    return new SessionManager(config);
};
